import { Box } from '@mui/material';
import { AI_GRADIENT_STOPS } from 'common/constants/ai';
import DRAWER from 'common/constants/drawer/Drawer.constants';
import { usePendingReviewCount } from 'store/useReviewStore';
import { AI } from 'theme/prismDesignTokens';

const AI_SPARKLE_GRADIENT_ID = 'review-ai-sparkle-gradient';

// Scalar AI sparkle, filled with the same purple -> blue gradient as the
// "Field Confidence" text (AI_GRADIENT_STOPS). The number sits to the RIGHT of it
// as plain readable text - overlaying the count on the sparkle was unreadable.
const AiSparkleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient id={AI_SPARKLE_GRADIENT_ID} x1="0" y1="0" x2="1" y2="0">
        {AI_GRADIENT_STOPS.map(stop => (
          <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
        ))}
      </linearGradient>
    </defs>
    <path
      d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM4.76465 10.3496C4.76466 10.8081 5.11309 11.185 5.55957 11.2305L5.65039 11.2354V11.4639C5.16135 11.4639 4.76485 11.8606 4.76465 12.3496H4.53613L4.53125 12.2588C4.48563 11.8126 4.10857 11.4641 3.65039 11.4639V11.2354L3.74121 11.2305C4.18746 11.1848 4.53612 10.8079 4.53613 10.3496H4.76465ZM8.52539 3.59961C8.52539 5.73971 10.2603 7.47461 12.4004 7.47461V8.47461C10.2604 8.47461 8.52559 10.2097 8.52539 12.3496H7.52539C7.52519 10.2098 5.79017 8.47485 3.65039 8.47461V7.47461C5.79029 7.47437 7.52539 5.73957 7.52539 3.59961H8.52539ZM4.65039 10.8379C4.54156 11.0603 4.36107 11.2407 4.13867 11.3496C4.36089 11.4584 4.54147 11.6382 4.65039 11.8604C4.75924 11.6386 4.93928 11.4584 5.16113 11.3496C4.93887 11.2407 4.75917 11.0602 4.65039 10.8379ZM8.02539 5.75C7.55027 6.7148 6.76552 7.49938 5.80078 7.97461C6.76535 8.44967 7.55018 9.23376 8.02539 10.1982C8.50052 9.23411 9.28483 8.44964 10.249 7.97461C9.28442 7.49937 8.50046 6.71471 8.02539 5.75ZM11.5146 3.59961C11.5147 4.05809 11.8631 4.43498 12.3096 4.48047L12.4004 4.48535V4.71387C11.9113 4.71387 11.5148 5.11061 11.5146 5.59961H11.2861L11.2812 5.50879C11.2356 5.0626 10.8586 4.7141 10.4004 4.71387V4.48535L10.4912 4.48047C10.9375 4.43476 11.2861 4.05793 11.2861 3.59961H11.5146ZM11.4004 4.08789C11.2916 4.31035 11.1111 4.49068 10.8887 4.59961C11.1109 4.70838 11.2915 4.88822 11.4004 5.11035C11.5092 4.88856 11.6893 4.70836 11.9111 4.59961C11.6889 4.49066 11.5092 4.31025 11.4004 4.08789Z"
      fill={`url(#${AI_SPARKLE_GRADIENT_ID})`}
    />
  </svg>
);

/**
 * Documents drawer tab title for the cap table, with a compact AI count badge.
 * Follows the NotesDrawerTabTitle precedent (tab title as a component). The count
 * is the number of PENDING review items only (accepted/dismissed items still live
 * in the store so they can render in the muted Reviewed section, but they must not
 * inflate the "needs review" count). The badge updates as items are accepted/declined.
 *
 * The count rides next to the gradient AI sparkle (the brand AI cue) rather than in
 * a generic oval, and is capped at "99+" so a large backlog never widens the tab
 * label. Cap-table-scoped: other drawers keep the plain Documents string.
 */
const ReviewDocumentsTabTitle = () => {
  const count = usePendingReviewCount();
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {DRAWER.TABS.DOCUMENT_REFERENCES.trim()}
      {count > 0 && (
        <Box
          component="span"
          title={`${count} need review`}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, lineHeight: 1 }}>
          <AiSparkleIcon />
          <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, color: AI }}>
            {count > 99 ? '99+' : count}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReviewDocumentsTabTitle;
